/**
 * 월별 상시 적용제외 근로자 명부
 * - 참여자 근무관리 마감된 달만 대상, 전체 사업단 기준
 */
import React, { useState } from 'react';
import api from '../services/api';

const baseStyles = {
  filtersContainer: {
    background: '#f8f9fa',
    padding: '15px',
    borderRadius: '5px',
    borderBottom: '1px solid #dee2e6',
  },
  filterGroup: { display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'flex-end' },
  filterItem: { display: 'flex', flexDirection: 'column', gap: '5px' },
  filterLabel: { fontSize: '0.8rem', fontWeight: 'bold', color: '#555', paddingLeft: '2px' },
  searchInput: { padding: '8px', fontSize: '0.9rem', width: '140px' },
  tableContainer: { marginTop: '20px', overflowX: 'auto' },
  table: { width: '100%', minWidth: '1000px', borderCollapse: 'collapse' },
  th: { border: '1px solid #ddd', padding: '8px', background: '#ffeb3b', color: '#333', textAlign: 'center', fontSize: '0.85rem' },
  td: { border: '1px solid #ddd', padding: '8px', fontSize: '0.85rem', textAlign: 'center' },
  searchButton: { backgroundColor: '#007bff', color: 'white', padding: '8px 16px', cursor: 'pointer', border: 'none', borderRadius: '4px' },
  excelButton: { backgroundColor: '#28a745', color: 'white', padding: '8px 16px', cursor: 'pointer', border: 'none', borderRadius: '4px' },
  countDisplay: { fontSize: '1rem', fontWeight: 'bold', color: '#333', marginTop: '15px', paddingLeft: '5px' },
};

const MONTH_OPTIONS = [
  { value: '', label: '전체' },
  ...Array.from({ length: 12 }, (_, i) => ({ value: String(i + 1), label: `${i + 1}월` })),
];

const ExcludedWorkerListPage = () => {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState('');
  const [list, setList] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const handleSearch = async () => {
    setHasSearched(true);
    setLoading(true);
    setError('');
    setMessage('');
    try {
      const params = new URLSearchParams({ year: selectedYear });
      if (selectedMonth) params.set('month', selectedMonth);
      const res = await api.get(`/excluded-workers?${params.toString()}`);
      setList(res.data.list || []);
    } catch (err) {
      setError(err.response?.data?.message || '목록 조회에 실패했습니다.');
      setList([]);
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = async () => {
    if (list.length === 0) {
      setError('출력할 데이터가 없습니다.');
      return;
    }
    setError('');
    try {
      const params = new URLSearchParams({ year: selectedYear });
      if (selectedMonth) params.set('month', selectedMonth);
      const res = await api.get(`/excluded-workers/export?${params.toString()}`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.setAttribute('download', `월별_상시_적용제외_근로자_명부_${selectedYear}.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
      setMessage('엑셀 파일이 다운로드되었습니다.');
    } catch (err) {
      let msg = '엑셀 출력에 실패했습니다.';
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const json = JSON.parse(text);
          msg = json.message || msg;
        } catch (_) {}
      } else if (err.response?.data?.message) {
        msg = err.response.data.message;
      }
      setError(msg);
    }
  };

  const formatWage = (v) => {
    if (v === '' || v == null) return '';
    const n = Number(v);
    return Number.isNaN(n) ? v : n.toLocaleString();
  };

  return (
    <div>
      <h2>월별 상시 적용제외 근로자 명부</h2>

      <div style={baseStyles.filtersContainer}>
        <div style={baseStyles.filterGroup}>
          <div style={baseStyles.filterItem}>
            <span style={baseStyles.filterLabel}>연도</span>
            <input
              type="number"
              style={baseStyles.searchInput}
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value, 10) || selectedYear)}
              min="2020"
            />
          </div>
          <div style={baseStyles.filterItem}>
            <span style={baseStyles.filterLabel}>월</span>
            <select
              style={{ ...baseStyles.searchInput, width: '100px' }}
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
            >
              {MONTH_OPTIONS.map((opt) => (
                <option key={opt.value || 'all'} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div style={baseStyles.filterItem}>
            <button style={baseStyles.searchButton} onClick={handleSearch} disabled={loading}>
              조회
            </button>
          </div>
          <div style={baseStyles.filterItem}>
            <button style={baseStyles.excelButton} onClick={handleExportExcel} disabled={loading || list.length === 0}>
              📥 엑셀 출력
            </button>
          </div>
        </div>
        <p style={{ fontSize: '0.85rem', color: '#666', marginTop: '10px', marginBottom: 0 }}>
          ※ 참여자 근무관리에서 마감한 달만 대상이며, 전체 사업단 기준으로 60시간 미만 근로자를 표시합니다.
        </p>
      </div>

      {error && <p style={{ color: 'red', marginTop: '10px' }}>{error}</p>}
      {message && <p style={{ color: 'green', marginTop: '10px' }}>{message}</p>}

      {!hasSearched ? (
        <p style={{ marginTop: '20px', color: '#666', fontSize: '0.95rem' }}>
          연도와 월을 선택한 후 조회 버튼을 클릭하세요.
        </p>
      ) : (
        <>
          <div style={baseStyles.countDisplay}>
            총 {list.length}건
          </div>

          <div style={baseStyles.tableContainer}>
            <table style={baseStyles.table}>
              <thead>
                <tr>
                  <th style={baseStyles.th}>연번</th>
                  <th style={baseStyles.th}>성명</th>
                  <th style={baseStyles.th}>적용제외 사유</th>
                  <th style={baseStyles.th}>임금지급기초일수 (또는 시간)</th>
                  <th style={baseStyles.th}>입사일자 (휴직시작일)</th>
                  <th style={baseStyles.th}>퇴사일자 (휴직복직일)</th>
                  <th style={baseStyles.th}>임금 (필수입력)</th>
                  <th style={baseStyles.th}>비고</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="8" style={{ textAlign: 'center', padding: '20px' }}>로딩 중...</td></tr>
                ) : list.length === 0 ? (
                  <tr><td colSpan="8" style={{ textAlign: 'center', padding: '20px', color: '#666' }}>데이터가 없습니다. 참여자 근무관리에서 해당 연도의 월을 마감해 주세요.</td></tr>
                ) : (
                  list.map((row, idx) => (
                    <tr key={idx}>
                      <td style={baseStyles.td}>{row.seq}</td>
                      <td style={baseStyles.td}>{row.name}</td>
                      <td style={baseStyles.td}>{row.exclusionReason}</td>
                      <td style={baseStyles.td}>{row.wageBasisDays}</td>
                      <td style={baseStyles.td}>{row.startDate || ''}</td>
                      <td style={baseStyles.td}>{row.endDate || ''}</td>
                      <td style={baseStyles.td}>{row.seq === '소계' ? '' : formatWage(row.wage)}</td>
                      <td style={baseStyles.td}>{row.remarks || ''}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default ExcludedWorkerListPage;
